<script setup>
import { ref, computed, onMounted } from 'vue';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { CONTENT_TYPES } from '../constants';
import FormattedContent from './Text/FormattedContent.vue';
import { detectMediaTones as sampleMediaTones } from './mediaTone';
import {
  classifyMediaAspectRatio,
  mediaFrameStyle,
  mediaAssetClass,
} from './mediaAspectLayout';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const {
  filteredCurrentChatAttachments,
  attachments,
  content,
  contentType,
  contentAttributes,
} = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();

const showGallery = ref(false);
const senderTone = ref('light');
const chevronTone = ref('light');
const timeTone = ref('light');
const isCaptionExpanded = ref(false);
const CAPTION_TRUNCATE_LENGTH = 768;
const caption = computed(() => {
  let value = String(content.value || '').trim();
  const normalizedValue = value.toLowerCase();
  const technicalMediaLabels = [
    'mensagem recebida',
    '[mensagem recebida]',
    'message received',
    '[message received]',
    'mídia recebida',
    '[mídia recebida]',
    'media received',
    '[media received]',
    '[sticker recebido]',
    '[sticker received]',
  ];

  if (technicalMediaLabels.includes(normalizedValue)) return '';

  // Aceleres: quando o rotulo tecnico vem colado como PREFIXO do texto real
  // (ex.: "[Mensagem recebida]\n\ntexto"), o match exato acima nao pega e
  // sobra o rotulo + linhas em branco antes da legenda, parecendo um vao
  // vazio na bolha. Corta so o prefixo tecnico nesse caso.
  const matchedLabel = technicalMediaLabels.find(label =>
    normalizedValue.startsWith(label)
  );
  if (matchedLabel) {
    value = value.slice(matchedLabel.length).trim();
  }

  return value;
});
const hasCaption = computed(() => Boolean(caption.value));
const isLongCaption = computed(
  () => caption.value.length >= CAPTION_TRUNCATE_LENGTH
);
const truncatedCaption = computed(() => {
  if (!isLongCaption.value) return caption.value;
  return [...caption.value].slice(0, CAPTION_TRUNCATE_LENGTH).join('');
});
const visibleCaption = computed(() =>
  isLongCaption.value && !isCaptionExpanded.value
    ? truncatedCaption.value
    : caption.value
);
const readMoreLabel = 'Ler mais';

const wameMediaType = computed(() =>
  String(
    contentAttributes.value?.wameMediaType ||
      contentAttributes.value?.wame_media_type ||
      ''
  ).toLowerCase()
);

const attachmentExtension = computed(() =>
  String(attachment.value?.extension || '').toLowerCase()
);

const attachmentContentType = computed(() =>
  String(
    attachment.value?.contentType || attachment.value?.content_type || ''
  ).toLowerCase()
);

const isSticker = computed(
  () =>
    contentType.value === CONTENT_TYPES.STICKER ||
    wameMediaType.value === 'sticker'
);

const isGif = computed(
  () =>
    !isSticker.value &&
    (wameMediaType.value === 'gif' ||
      attachmentExtension.value === 'gif' ||
      attachmentContentType.value === 'image/gif')
);

const loadedImageWidth = ref(0);
const loadedImageHeight = ref(0);

const naturalImageDimensions = computed(() => ({
  width: loadedImageWidth.value || Number(attachment.value?.width || 0),
  height: loadedImageHeight.value || Number(attachment.value?.height || 0),
}));

const imageAspectRatio = computed(() => {
  const { width, height } = naturalImageDimensions.value;

  return width > 0 && height > 0 ? width / height : null;
});

// Aceleres: classificacao pelo aspect ratio real (nunca pela altura em
// pixels isolada), igual ao WhatsApp — compartilhada com Video.vue.
const imageAspectClass = computed(() => {
  if (isSticker.value || isGif.value) return null;
  return classifyMediaAspectRatio(imageAspectRatio.value);
});

const imageAssetFrameStyle = computed(() => {
  if (!imageAspectClass.value) return {};
  return mediaFrameStyle(imageAspectClass.value);
});

const imageAssetClass = computed(() => {
  // Aceleres: mesmo antes de classificar o aspect ratio (janela entre
  // isLoaded=true e loadedImageWidth/Height ainda em 0), o fallback precisa
  // do mesmo teto de altura do bucket final — senao o frame (fit-content)
  // chega a desenhar mais alto por um instante e some so depois, mas em
  // alguns casos essa reflow nao acontece de verdade, deixando vao vazio.
  if (!imageAspectClass.value) return 'h-auto w-auto max-w-full max-h-[344px] object-contain !bg-white';
  return `${mediaAssetClass(imageAspectClass.value)} !bg-white`;
});

const imageDimensionsOrOriginal = computed(() => ({
  width: naturalImageDimensions.value.width || undefined,
  height: naturalImageDimensions.value.height || undefined,
}));

onMounted(async () => {
  if (attachment.value?.dataUrl) {
    const dimensions = await loadWithRetry(attachment.value.dataUrl);
    loadedImageWidth.value = Number(dimensions?.width || 0);
    loadedImageHeight.value = Number(dimensions?.height || 0);
  }
});

const handleImageError = () => {
  hasError.value = true;
};

const detectImageTones = event => {
  try {
    const image = event.currentTarget;
    const tones = sampleMediaTones(image);
    senderTone.value = tones.sender;
    chevronTone.value = tones.chevron;
    timeTone.value = tones.time;
  } catch {
    senderTone.value = 'light';
    chevronTone.value = 'light';
    timeTone.value = 'light';
  }
};

const handleImageLoad = event => {
  loadedImageWidth.value = Number(event.currentTarget?.naturalWidth || 0);
  loadedImageHeight.value = Number(event.currentTarget?.naturalHeight || 0);
  detectImageTones(event);
};
</script>

<template>
  <BaseBubble
    :class="[
      isSticker
        ? 'acl-sticker-bubble max-w-[min(320px,70vw)] p-0'
        : isGif
          ? 'acl-gif-bubble p-1'
          : 'acl-image-bubble !w-fit !max-w-[calc(100vw-48px)]',
      !isSticker && !isGif ? 'p-0' : '',
      hasError ? 'w-[260px] max-w-[calc(100vw-5rem)]' : '',
    ]"
    show-sender-name
    media-caption
    :data-bubble-name="isSticker ? 'sticker' : isGif ? 'gif' : 'image'"
    :data-media-state="hasError ? 'unavailable' : undefined"
    :data-aspect-class="imageAspectClass || undefined"
    :data-sender-tone="senderTone"
    :data-chevron-tone="chevronTone"
    :data-time-tone="timeTone"
    role="button"
    tabindex="0"
    @click="showGallery = true"
    @keydown.enter.space.self.prevent="showGallery = true"
  >
    <div
      v-if="hasError"
      class="acl-image-unavailable flex min-h-16 items-center gap-2 rounded-lg px-3 py-4 text-left"
    >
      <Icon
        icon="i-lucide-circle-off"
        class="size-4 flex-shrink-0 text-n-slate-11"
      />
      <p class="mb-0 min-w-0 text-sm leading-5 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="isLoaded"
      class="acl-image-bubble__asset-frame relative group overflow-hidden"
      :class="{
        'rounded-lg': !isSticker,
      }"
      :style="imageAssetFrameStyle"
    >
      <img
        class="block skip-context-menu"
        :class="[
          isSticker
            ? 'max-h-[280px] max-w-full w-auto object-contain'
            : imageAssetClass,
        ]"
        :src="attachment.dataUrl"
        :width="imageDimensionsOrOriginal.width"
        :height="imageDimensionsOrOriginal.height"
        :alt="$t('CHAT_LIST.ATTACHMENTS.image.CONTENT')"
        @load="handleImageLoad"
      />
      <div
        v-if="!isSticker && !isGif"
        class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-24 bg-gradient-to-t from-black/45 via-black/10 to-transparent opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      />
      <div
        v-if="!isSticker && !isGif"
        class="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-black/30 via-black/10 to-transparent opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      />
    </div>
    <div
      v-if="hasCaption"
      class="acl-image-bubble__caption box-border w-full min-h-9 px-2 pb-6 pt-2 text-[15.8px] leading-5"
      @click.stop
    >
      <span class="inline">
        <FormattedContent
          class="acl-message-caption [&_p]:!inline [&_p]:!m-0"
          :content="visibleCaption"
        />
        <button
          v-if="isLongCaption && !isCaptionExpanded"
          type="button"
          class="inline font-bold text-[#1b8755] no-underline -ml-px !m-0 !p-0 border-0 leading-none"
          @click.stop="isCaptionExpanded = true"
        >
          ... {{ readMoreLabel }}
        </button>
      </span>
    </div>
    <slot name="menu-trigger" :chevron-tone="chevronTone" />
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    :initial-caption="caption"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
